import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { unique } from 'radash';
import { useParams } from 'react-router-dom';
import {
    GET_PROJECT_MILESTONES_BY_PROJECT_TYPE_ID,
    RelatedFormFieldQuery,
} from '@api/queries/projects';
import useUser from '@hooks/useUser';
import { mapQueryResultToOptions } from '@pages/projects/_utils/utils/mapQueryResultToStringOption';
import { type StringOption } from '@models/Option';
import { GET_USERS_FOR_SELECT } from '@api/queries/users';
import { useChunkedQueryConnection } from '@models/useChunkedQueryConnection';
import { type ProjectFormAttributes } from '../../ProjectForm';

export interface ProjectFormDetailsState {
    pid?: string;
    currentUser: ReturnType<typeof useUser>;
    usersLoading: boolean;
    milestonesLoading: boolean;
    relatedLoading: boolean;
    showStepLoadingOverlay: boolean;
    showDeprecated: boolean;
    hasDeprecatedTypes: boolean;
    projectTypeOptions: StringOption[];
    statusOptions: StringOption[];
    organizationOptions: StringOption[];
    projectMilestonesOptions: StringOption[];
    projectMembersOptions: StringOption[];
    validateMilestone: (value: string | undefined) => string | true;
    toggleDeprecatedTypes: () => void;
}

export function useProjectFormDetailsState(): ProjectFormDetailsState {
    const {
        watch,
        setValue,
        getValues,
        formState: { isDirty },
    } = useFormContext<ProjectFormAttributes>();
    const { pid } = useParams();
    const id = watch('id');
    const projectId = pid ?? id;
    const userIDs = watch('userIDs');
    const securityResponsibleID = watch('securityResponsibleID');
    const typeID = watch('typeID', '');

    const selectedIds = useMemo(
        () =>
            unique(
                [
                    ...userIDs,
                    ...(securityResponsibleID ? [securityResponsibleID] : []),
                ].filter(Boolean),
            ),
        [userIDs, securityResponsibleID],
    );

    // The selection can exceed one page; chunking resolves every selected user
    // instead of silently truncating at the page-size ceiling.
    const { nodes: users, loading: usersLoading } = useChunkedQueryConnection(
        GET_USERS_FOR_SELECT,
        selectedIds,
        (chunk) => ({
            first: chunk.length,
            where: { active: true, idIn: chunk },
        }),
        { rootField: 'users', skip: selectedIds.length === 0 },
    );

    const { data: projectMilestonesData, loading: milestonesLoading } =
        useQuery(GET_PROJECT_MILESTONES_BY_PROJECT_TYPE_ID, {
            skip: !typeID,
            variables: { projectTypeId: typeID },
        });

    const relatedQuery = useQuery(RelatedFormFieldQuery);
    const currentUser = useUser();

    const projectMembersOptions: StringOption[] = useMemo(() => {
        const selectedIdsSet = new Set(selectedIds);

        return users
            .filter((user) => selectedIdsSet.has(user.id))
            .map((user) => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName}`,
            }));
    }, [users, selectedIds]);

    const { organizations, projectStatuses, projectTypes } =
        relatedQuery.data ?? {};

    const projectTypeOptions = mapQueryResultToOptions(projectTypes);
    const statusOptions = mapQueryResultToOptions(projectStatuses);

    const organizationOptions = useMemo(
        () => mapQueryResultToOptions(organizations),
        [organizations],
    );

    const projectMilestonesOptions = useMemo(
        () =>
            projectMilestonesData
                ? mapQueryResultToOptions(
                      projectMilestonesData.projectMilestones,
                  )
                : [],
        [projectMilestonesData],
    );

    const validMilestoneIds = useMemo(
        () => projectMilestonesOptions.map((option) => option.value),
        [projectMilestonesOptions],
    );

    const validateMilestone = useCallback(
        (value: string | undefined) => {
            if (!value) {
                return 'Please select a project milestone';
            }
            if (
                projectMilestonesOptions.length > 0 &&
                !validMilestoneIds.includes(value)
            ) {
                return 'Selected milestone is not valid for this project type';
            }

            return true;
        },
        [projectMilestonesOptions, validMilestoneIds],
    );

    useEffect(() => {
        const currentMilestone = getValues('milestoneID');
        if (
            projectMilestonesOptions.length > 0 &&
            currentMilestone &&
            !validMilestoneIds.includes(currentMilestone)
        ) {
            setValue('milestoneID', '', { shouldValidate: true });
        }
    }, [projectMilestonesOptions, validMilestoneIds, getValues, setValue]);

    useEffect(() => {
        if (!currentUser || isDirty || !organizationOptions.length) {
            return;
        }
        const userOrg = organizationOptions.find(
            (org) => org.label === currentUser.orgCode,
        );
        if (userOrg) {
            setValue('organizationID', userOrg.value);
        }
    }, [organizationOptions, currentUser, isDirty, setValue]);

    useEffect(() => {
        if (!projectId && currentUser) {
            setValue('securityResponsibleID', currentUser.id);
        }
    }, [projectId, currentUser, setValue]);

    const [showDeprecated, setShowDeprecated] = useState(false);

    const hasDeprecatedTypes = projectTypeOptions.some((option) =>
        option.label.toLowerCase().startsWith('deprecated -'),
    );

    const relatedLoading = relatedQuery.loading;
    const showStepLoadingOverlay =
        (relatedLoading && !relatedQuery.data) ||
        (usersLoading && users.length === 0);

    const toggleDeprecatedTypes = (): void => {
        setShowDeprecated((previousValue) => !previousValue);
    };

    return {
        pid: projectId,
        currentUser,
        usersLoading,
        milestonesLoading,
        relatedLoading,
        showStepLoadingOverlay,
        showDeprecated,
        hasDeprecatedTypes,
        projectTypeOptions,
        statusOptions,
        organizationOptions,
        projectMilestonesOptions,
        projectMembersOptions,
        validateMilestone,
        toggleDeprecatedTypes,
    };
}
